/** React Query wrappers for the time-off vs staffing warnings (services/timeOffConflicts.ts). Warnings only. */
import { useQuery } from "@tanstack/react-query";
import {
  getEventLocalDates,
  getTimeOffConflicts,
} from "../services/timeOffConflicts";
import {
  getEventSalesAssignments,
  getEventSalesAssignmentDays,
} from "../services/salesCalendar.service";

/** positionId -> local dates (from `dates`) the rep has time off. */
export function useTimeOff(positionIds: string[], dates: string[]) {
  const ids = [...new Set(positionIds.filter(Boolean))].sort();
  const ds = [...new Set(dates.filter(Boolean))].sort();
  return useQuery({
    queryKey: ["time-off", ids.join(","), ds.join(",")],
    queryFn: () => getTimeOffConflicts(ids, ds),
    enabled: ids.length > 0 && ds.length > 0,
    staleTime: 30_000,
  });
}

export function useEventLocalDates(eventId: string | undefined) {
  return useQuery({
    queryKey: ["event-local-dates", eventId],
    queryFn: () => getEventLocalDates(eventId as string),
    enabled: !!eventId,
    staleTime: 30_000,
  });
}

export interface StaffingTimeOffConflict {
  positionId: string;
  name: string;
  dates: string[];
}

/**
 * Existing assignments on this event that fall on a rep's time off (e.g. the event was moved onto a day off): an
 * all-days rep conflicts on any event date; a per-day rep only on the days they are ticked for.
 */
export function useEventTimeOffConflicts(eventId: string | undefined) {
  const assignments = useQuery({
    queryKey: ["event-sales-assignments", eventId],
    queryFn: () => getEventSalesAssignments(eventId as string),
    enabled: !!eventId,
  });
  const hasPerDay = (assignments.data ?? []).some(
    (a) => a.day_scope === "selected",
  );
  const repDays = useQuery({
    queryKey: ["event-sales-assignment-days", eventId],
    queryFn: () => getEventSalesAssignmentDays(eventId as string),
    enabled: !!eventId && hasPerDay,
  });
  const dates = useEventLocalDates(eventId);
  const timeOff = useTimeOff(
    (assignments.data ?? []).map((a) => a.sales_position_id),
    dates.data ?? [],
  );
  const conflicts: StaffingTimeOffConflict[] = [];
  for (const a of assignments.data ?? []) {
    const off = timeOff.data?.[a.sales_position_id] ?? [];
    const works =
      a.day_scope === "selected"
        ? (repDays.data?.[a.id] ?? [])
        : (dates.data ?? []);
    const hit = off.filter((d) => works.includes(d));
    if (hit.length) {
      conflicts.push({
        positionId: a.sales_position_id,
        name:
          a.sales_position?.assigned_name || a.sales_position?.title || "A rep",
        dates: hit,
      });
    }
  }
  return { conflicts, timeOff: timeOff.data ?? {}, dates: dates.data ?? [] };
}
